class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementsByTagName('body')[0].classList.add('facet-filters-loading');

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document.getElementsByTagName('body')[0].classList.remove('facet-filters-loading');

    productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Restore view switcher state after grid update
    FacetFiltersForm.restoreViewSwitcherState();

    // Scroll to top of grid after render with margin
    productGridContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCountDesktop').innerHTML;
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg vertical or drawer)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  static restoreViewSwitcherState() {
    const savedColumns = localStorage.getItem('productViewColumns');
    if (savedColumns) {
      const columns = parseInt(savedColumns);
      const productGrid = document.getElementById('product-grid');
      if (productGrid) {
        // Remove existing column classes
        productGrid.classList.remove(
          'grid--1-col-desktop',
          'grid--2-col-desktop',
          'grid--3-col-desktop',
          'grid--4-col-desktop',
          'grid--1-col-tablet',
          'grid--2-col-tablet',
          'grid--3-col-tablet',
          'grid--1-col-mobile',
          'grid--2-col-mobile'
        );

        // Add new column classes based on responsive breakpoints
        const windowWidth = window.innerWidth;

        if (columns === 1) {
          // List view - single column
          productGrid.classList.add('grid--1-col-desktop');
          productGrid.classList.add('grid--1-col-tablet');
          productGrid.classList.add('grid--1-col-mobile');

          // Add list-item class to grid items
          const gridItems = productGrid.querySelectorAll('.grid__item');
          gridItems.forEach((item) => {
            item.classList.add('list-item');
          });
        } else {
          // Grid view - responsive columns
          if (windowWidth > 1400) {
            // Desktop large: use selected columns (up to 4)
            productGrid.classList.add(`grid--${Math.min(columns, 4)}-col-desktop`);
            productGrid.classList.add('grid--3-col-tablet');
            productGrid.classList.add('grid--1-col-mobile');
          } else if (windowWidth > 992) {
            // Desktop medium: use selected columns (up to 3)
            productGrid.classList.add(`grid--${Math.min(columns, 3)}-col-desktop`);
            productGrid.classList.add('grid--2-col-tablet');
            productGrid.classList.add('grid--1-col-mobile');
          } else if (windowWidth > 560) {
            // Tablet: use selected columns (up to 2)
            productGrid.classList.add(`grid--${Math.min(columns, 2)}-col-desktop`);
            productGrid.classList.add('grid--2-col-tablet');
            productGrid.classList.add('grid--1-col-mobile');
          } else {
            // Mobile: single column
            productGrid.classList.add('grid--1-col-desktop');
            productGrid.classList.add('grid--1-col-tablet');
            productGrid.classList.add('grid--1-col-mobile');
          }

          // Remove list-item class from grid items
          const gridItems = productGrid.querySelectorAll('.grid__item');
          gridItems.forEach((item) => {
            item.classList.remove('list-item');
          });
        }
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetDrawerToggle extends HTMLElement {
  constructor() {
    super();

    const facetsDrawerOpen = this.querySelector('.facets-drawer__open');
    if (facetsDrawerOpen) {
      facetsDrawerOpen.addEventListener('click', this.openFacetsDrawer.bind(this));
      facetsDrawerOpen.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.openFacetsDrawer();
      });
    }

    const facetsDrawerClose = this.querySelector('.facets-drawer__close');
    if (facetsDrawerClose) {
      facetsDrawerClose.addEventListener('click', this.closeFacetsDrawer.bind(this));
      facetsDrawerClose.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFacetsDrawer();
      });
    }

    this.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  openFacetsDrawer() {
    const facetsDrawer = document.querySelector('.main-collection-wrapper');
    if (facetsDrawer) facetsDrawer.classList.add('facets-drawer--open');
  }

  closeFacetsDrawer() {
    const facetsDrawer = document.querySelector('.main-collection-wrapper');
    if (facetsDrawer) facetsDrawer.classList.remove('facets-drawer--open');
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.closeFacetsDrawer();
  }
}
customElements.define('facet-drawer-toggle', FacetDrawerToggle);

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('input[type="range"]:first-child');
    this.rangeMax = this.querySelector('input[type="range"]:last-child');
    this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
    this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

    this.inputMin.addEventListener('focus', this.inputMin.select);
    this.inputMax.addEventListener('focus', this.inputMax.select);
    this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
    this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

    this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
    this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
    this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
    this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
  }

  onInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1),
      event.target.min
    );
    this.rangeMin.value = event.target.value;
    this.rangeMin.parentElement.style.setProperty(
      '--from',
      `${(parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100}%`
    );
  }

  onInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1),
      event.target.max
    );
    this.rangeMax.value = event.target.value;
    this.rangeMax.parentElement.style.setProperty(
      '--to',
      `${(parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100}%`
    );
  }

  onRangeMinChange(event) {
    event.stopPropagation();
    this.inputMin.value = event.target.value;
    this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMaxChange(event) {
    event.stopPropagation();
    this.inputMax.value = event.target.value;
    this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMinInput(event) {
    event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
    event.target.parentElement.style.setProperty(
      '--from',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.inputMin.value = event.target.value;
  }

  onRangeMaxInput(event) {
    event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
    event.target.parentElement.style.setProperty(
      '--to',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.inputMax.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

class FacetViewSwitcher extends HTMLElement {
  constructor() {
    super();
    this.onSwitchClick = this.onSwitchClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.buttons = this.querySelectorAll('button[data-column]');

    // Add event listeners to all buttons
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onSwitchClick);
    });

    // Add resize event listener
    window.addEventListener('resize', this.onResize);

    // Initialize with saved preference or default
    this.initializeView();
  }

  initializeView() {
    const savedColumns = localStorage.getItem('productViewColumns');
    if (savedColumns && !window.location.search.includes('skip-view-columns')) {
      // Update active state of buttons based on saved preference
      this.buttons.forEach((btn) => {
        btn.classList.remove('active');
        if (parseInt(btn.dataset.column) === parseInt(savedColumns)) {
          btn.classList.add('active');
        }
      });
      this.updateGridLayout(parseInt(savedColumns));
    } else {
      // Apply responsive layout based on current window width
      const windowWidth = window.innerWidth;
      if (windowWidth < 1400) {
        this.applyResponsiveLayout();
      }
    }
  }

  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.applyResponsiveLayout();
    }, 150);
  }

  applyResponsiveLayout() {
    const windowWidth = window.innerWidth;
    let columns = 1;

    if (windowWidth > 1400) {
      columns = 4;
    } else if (windowWidth > 992) {
      columns = 3;
    } else if (windowWidth > 560) {
      columns = 2;
    } else {
      columns = 1;
    }

    // Update active button state
    this.buttons.forEach((btn) => {
      btn.classList.remove('active');
      if (parseInt(btn.dataset.column) === columns) {
        btn.classList.add('active');
      }
      if (parseInt(btn.dataset.column) > columns) {
        btn.style.display = 'none';
      } else {
        btn.style.display = 'inline-flex';
      }
    });

    // Update grid layout
    this.updateGridLayout(columns);
  }

  onSwitchClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const columns = parseInt(button.dataset.column);

    if (!columns) return;

    // Update active state of buttons
    this.buttons.forEach((btn) => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    // Update product grid layout
    this.updateGridLayout(columns);

    // Store preference
    localStorage.setItem('productViewColumns', columns.toString());
  }

  updateGridLayout(columns) {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    // Remove existing column classes
    productGrid.classList.remove(
      'grid--1-col-desktop',
      'grid--2-col-desktop',
      'grid--3-col-desktop',
      'grid--4-col-desktop',
      'grid--1-col-tablet',
      'grid--2-col-tablet',
      'grid--3-col-tablet',
      'grid--1-col-mobile',
      'grid--2-col-mobile'
    );

    // Add new column classes based on responsive breakpoints
    const windowWidth = window.innerWidth;

    if (columns === 1) {
      // List view - single column
      productGrid.classList.add('grid--1-col-desktop');
      productGrid.classList.add('grid--1-col-tablet');
      productGrid.classList.add('grid--1-col-mobile');

      // Add list-item class to grid items
      const gridItems = productGrid.querySelectorAll('.grid__item');
      gridItems.forEach((item) => {
        item.classList.add('list-item');
      });
    } else {
      // Grid view - responsive columns
      if (windowWidth > 1400) {
        productGrid.classList.add(`grid--${Math.min(columns, 4)}-col-desktop`);
        productGrid.classList.add('grid--3-col-tablet');
        productGrid.classList.add('grid--1-col-mobile');
      } else if (windowWidth > 992) {
        productGrid.classList.add(`grid--${Math.min(columns, 3)}-col-desktop`);
        productGrid.classList.add('grid--2-col-tablet');
        productGrid.classList.add('grid--1-col-mobile');
      } else if (windowWidth > 560) {
        // Tablet: use selected columns (up to 2)
        productGrid.classList.add(`grid--${Math.min(columns, 2)}-col-desktop`);
        productGrid.classList.add('grid--2-col-tablet');
        productGrid.classList.add('grid--1-col-mobile');
      } else {
        // Mobile: single column
        productGrid.classList.add('grid--1-col-desktop');
        productGrid.classList.add('grid--1-col-tablet');
        productGrid.classList.add('grid--1-col-mobile');
      }

      // Remove list-item class from grid items
      const gridItems = productGrid.querySelectorAll('.grid__item');
      gridItems.forEach((item) => {
        item.classList.remove('list-item');
      });
    }
  }

  disconnectedCallback() {
    // Clean up resize event listener when element is removed
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
  }
}
customElements.define('facet-view-switcher', FacetViewSwitcher);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    button.addEventListener('click', (event) => {
      this.expandShowMore(event);
      const nextElementToFocus = event.target.closest('.parent-display').querySelector('.show-more-item');
      if (
        nextElementToFocus &&
        !nextElementToFocus.classList.contains('hidden') &&
        nextElementToFocus.querySelector('input')
      ) {
        nextElementToFocus.querySelector('input').focus();
      }
    });
  }
  expandShowMore(event) {
    const parentDisplay = event.target.closest('[id^="Show-More-"]').closest('.parent-display');
    const parentWrap = parentDisplay.querySelector('.parent-wrap');
    this.querySelectorAll('.label-text').forEach((element) => element.classList.toggle('hidden'));
    parentDisplay.querySelectorAll('.show-more-item').forEach((item) => item.classList.toggle('hidden'));
    if (!this.querySelector('.label-show-less')) {
      this.classList.add('hidden');
    }
  }
}
customElements.define('show-more-button', ShowMoreButton);
